import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MagnifyingGlass from '@/assets/icons/magnifying_glass.svg?react';
import CloseX from '@/assets/icons/close-x.svg?react';
import { useDebouncedInput } from '@/hooks/useDebouncedInput';
import { PlayerAutosuggest, type PlayerSuggestion } from '@/common/PlayerAutosuggest/PlayerAutosuggest';
import styles from './SearchTextFilter.module.scss';

export type SearchMode = 'player' | 'order' | 'serial';

export interface OrdersSearchCriteria {
  playerId?: number;
  playerName?: string;
  orderNo?: string;
  laserNo?: string;
}

interface IProps {
  mode: SearchMode;
  modes?: SearchMode[];
  /* eslint-disable no-unused-vars */
  onModeChange: (mode: SearchMode) => void;
  onCommit: (criteria: OrdersSearchCriteria) => void;
  /* eslint-enable no-unused-vars */
}

export default function SearchTextFilter({ mode, modes = ['player', 'order', 'serial'], onModeChange, onCommit }: IProps) {
  const [text, setText] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusOnModeChange = useRef(false);
  const pendingSearch = useMemo(() => ({ mode, text }), [mode, text]);
  const debouncedSearch = useDebouncedInput(pendingSearch, 300);
  const isPlayerMode = mode === 'player';
  const showPlayerMode = modes.includes('player');
  const showOrderMode = modes.includes('order');
  const showSerialMode = modes.includes('serial');
  const wrapperClass = `${styles.searchWrapper} ${showPlayerMode ? '' : styles.withoutPlayer}`;
  const playerButtonClass = `${styles.filterButton} ${styles.player} ${isPlayerMode ? styles.selected : ''}`;
  const orderButtonClass = `${styles.filterButton} ${styles.order} ${mode === 'order' ? styles.selected : ''}`;
  const serialButtonClass = `${styles.filterButton} ${styles.serial} ${mode === 'serial' ? styles.selected : ''}`;

  const handleModeChange = (nextMode: SearchMode) => {
    inputRef.current?.focus();
    if (nextMode === mode) return;
    focusOnModeChange.current = true;
    setText('');
    onModeChange(nextMode);
  };

  const handleClear = () => {
    setText('');
    onCommit({});
    inputRef.current?.focus();
  };

  const handlePlayerSelect = useCallback(
    (player: PlayerSuggestion | undefined) => {
      onCommit(player ? { playerId: player.playerId } : {});
    },
    [onCommit],
  );
  const handlePlayerSearch = useCallback(
    (playerName: string | undefined) => {
      onCommit({ playerName });
    },
    [onCommit],
  );

  useEffect(() => {
    if (!focusOnModeChange.current) return;
    inputRef.current?.focus();
    focusOnModeChange.current = false;
  }, [mode]);

  useEffect(() => {
    if (debouncedSearch.mode !== mode) return;
    const trimmedText = debouncedSearch.text.trim();
    if (mode === 'order') onCommit({ orderNo: trimmedText || undefined });
    if (mode === 'serial') onCommit({ laserNo: trimmedText || undefined });
  }, [debouncedSearch, mode, onCommit]);

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      {isPlayerMode && <PlayerAutosuggest inputRef={inputRef} wrapperRef={wrapperRef} onSelect={handlePlayerSelect} onSearch={handlePlayerSearch} />}
      {!isPlayerMode && (
        <fieldset className={styles.field}>
          <legend className={styles.legend}>&nbsp;</legend>
          <div className={styles.control}>
            <MagnifyingGlass className={styles.magnifyingGlass} />
            <input ref={inputRef} type="text" aria-label={`Search by ${mode}`} autoComplete="off" className={styles.filterInput} onChange={(event) => setText(event.target.value)} value={text} />
            {text !== '' && (
              <button type="button" className={styles.clearButton} aria-label="Clear search" onClick={handleClear}>
                <CloseX className={styles.closeX} />
              </button>
            )}
          </div>
        </fieldset>
      )}
      {showPlayerMode && (
        <button type="button" className={playerButtonClass} onClick={() => handleModeChange('player')}>
          Player
        </button>
      )}
      {showOrderMode && (
        <button type="button" className={orderButtonClass} onClick={() => handleModeChange('order')}>
          Order
        </button>
      )}
      {showSerialMode && (
        <button type="button" className={serialButtonClass} onClick={() => handleModeChange('serial')}>
          Serial
        </button>
      )}
    </div>
  );
}
